import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { Grid, GridProps, IconButton } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { CSS } from '@dnd-kit/utilities';

type RenderFunctionProps = {
  isHoveringOver: boolean;
};

type DraggableProps = {
  id: string;
  disabled?: boolean;
  containerProps?: GridProps;
  onHoverChange?: (isHover: boolean) => void;
};

type DraggablePropsWithChildren = {
  children: React.ReactNode;
  render?: never;
};
type DraggablePropsWithRenderProp = {
  render: (props: RenderFunctionProps) => React.ReactNode;
  children?: never;
};

type Props = (DraggablePropsWithChildren | DraggablePropsWithRenderProp) &
  DraggableProps;

function Draggable({
  children,
  render,
  id,
  disabled = false,
  containerProps = {},
  onHoverChange,
}: Props) {
  const [isHover, serIsHover] = React.useState(false);
  const { attributes, setNodeRef, listeners, transform } = useSortable({
    id: id,
    disabled,
  });

  const handleMouseEnter = () => {
    serIsHover(true);
    onHoverChange && onHoverChange(true);
  };

  const handleMouseLeave = () => {
    serIsHover(false);
    onHoverChange && onHoverChange(false);
  };

  const getDragHandle = () => {
    if (disabled) {
      return null;
    }

    return (
      <Grid
        item
        sx={{ pointerEvents: 'all', position: 'relative', zIndex: 1000 }}
      >
        <IconButton
          {...listeners}
          disableRipple={true}
          sx={{
            cursor: 'grab',
            display: disabled ? 'none' : 'block',
            opacity: isHover ? 0.8 : 0,
            transform: 'translate(-50%)',
            transition: (theme) =>
              theme.transitions.create('opacity', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
          }}
        >
          <DragIndicatorIcon />
        </IconButton>
      </Grid>
    );
  };

  return (
    <Grid
      item
      container
      ref={setNodeRef}
      sx={{
        transform: CSS.Translate.toString(transform),
        '&:focus-visible': { outline: 'none' },
      }}
      alignItems='center'
      flexWrap='nowrap'
      {...attributes}
      {...containerProps}
      component='div'
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {getDragHandle()}
      <Grid item flex={1} container ml={-3.5}>
        {render
          ? render({
              isHoveringOver: isHover,
            })
          : children}
      </Grid>
    </Grid>
  );
}

export default Draggable;
